import React, { useState, useEffect } from 'react';
import { PAGE, SettingsHeader } from './SettingsUI';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import BranchAccess from './BranchAccess';
import toast from 'react-hot-toast';
import {
  MapPin, Plus, Edit2, Trash2, Users, UserCheck, Calendar,
  X, Save, ChevronRight, Building2, Phone, Mail, Globe, ShieldCheck } from 'lucide-react';
import AddressAutocomplete from '../../components/AddressAutocomplete';

const TIMEZONES = [
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane',
  'Australia/Perth', 'Australia/Adelaide', 'Australia/Darwin',
  'Australia/Hobart',
];

const EMPTY = { name: '', code: '', address: '', phone: '', email: '', manager: '', timezone: 'Australia/Sydney', is_active: true };

function LocationForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    await onSave(form);
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
      <h3 className="text-lg font-bold text-slate-800 mb-5">{initial?.id ? 'Edit Location' : 'New Location'}</h3>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 sm:col-span-1">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Location Name *</label>
          <input value={form.name} onChange={set('name')} placeholder="e.g. Sydney Office"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Short Code</label>
          <input value={form.code} onChange={set('code')} placeholder="e.g. SYD"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Address</label>
          <AddressAutocomplete value={form.address}
            onChange={v => setForm(f => ({ ...f, address: v }))}
            onSelect={a => setForm(f => ({ ...f, address: [a.street, a.suburb, a.state, a.postcode].filter(Boolean).join(', '), latitude: a.lat, longitude: a.lon }))}
            placeholder="Street address"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Phone</label>
          <input value={form.phone} onChange={set('phone')} placeholder="02 xxxx xxxx"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Email</label>
          <input value={form.email} onChange={set('email')} type="email" placeholder="location@example.com"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Location Manager</label>
          <input value={form.manager} onChange={set('manager')} placeholder="Manager name"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Timezone</label>
          <select value={form.timezone} onChange={set('timezone')}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
            {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
      </div>
      <div className="flex gap-3 mt-6 justify-end">
        <button type="button" onClick={onCancel}
          className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
        <button type="submit"
          className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-2">
          <Save size={14} /> Save Location
        </button>
      </div>
    </form>
  );
}

function StatChip({ icon: Icon, value, label, color }) {
  return (
    <div className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${color}`}>
      <Icon size={11} /> {value} {label}
    </div>
  );
}

export default function Locations() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [accessFor, setAccessFor] = useState(null);
  const [usage, setUsage]         = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/locations');
      setLocations(data);
    } catch { toast.error('Failed to load locations'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Branch capacity is part of the plan, so show it here rather than making someone find
  // out by being refused when they add one.
  useEffect(() => {
    api.get('/billing/subscription')
      .then(({ data }) => setUsage(data?.usage || null))
      .catch(() => {});
  }, []);

  const handleSave = async (form) => {
    try {
      if (editing?.id) {
        await api.put(`/locations/${editing.id}`, form);
        toast.success('Location updated');
      } else {
        await api.post('/locations', form);
        toast.success('Location created');
      }
      setShowForm(false); setEditing(null);
      load();
    } catch (e) {
      const d = e.response?.data;
      if (d?.code === 'PLAN_LIMIT_REACHED') toast.error(d.message, { duration: 7000 });
      else toast.error(d?.message || 'Save failed');
    }
  };

  const handleDelete = async (loc) => {
    if (!confirm(`Delete "${loc.name}"? Staff and clients will be unassigned.`)) return;
    try {
      await api.delete(`/locations/${loc.id}`);
      toast.success('Location deleted');
      load();
    } catch (e) {
      const d = e.response?.data;
      // The refusal names who would be affected, so show that rather than "Delete failed".
      if (d?.code === 'BRANCH_HAS_SOLE_MEMBERS') toast.error(d.message, { duration: 9000 });
      else toast.error(d?.message || 'Delete failed');
    }
  };

  const toggleActive = async (loc) => {
    try {
      await api.put(`/locations/${loc.id}`, { ...loc, is_active: !loc.is_active });
      load();
    } catch { toast.error('Update failed'); }
  };

  return (
    <div className={PAGE}>
      <SettingsHeader
        title="Locations & branches"
        description="Offices, branches and franchise sites."
        after={usage?.branch_limit != null && (
          <p className="mt-1 text-xs tabular-nums text-slate-500">
            {usage.branches} of {usage.branch_limit} branches used on your plan
          </p>
        )}
        actions={
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary">
            <Plus size={15} /> Add Location
          </button>
        }
      />

      {/* Summary bar */}
      {!loading && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Total Locations', value: locations.length, color: 'bg-indigo-50 text-indigo-700' },
            { label: 'Active', value: locations.filter(l => l.is_active).length, color: 'bg-emerald-50 text-emerald-700' },
            { label: 'Total Staff', value: locations.reduce((a, l) => a + parseInt(l.staff_count || 0), 0), color: 'bg-sky-50 text-sky-700' },
          ].map(s => (
            <div key={s.label} className={`rounded-xl p-4 ${s.color}`}>
              <div className="text-2xl font-bold">{s.value}</div>
              <div className="text-xs font-semibold opacity-70 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Form */}
      {accessFor && (
        <BranchAccess branch={accessFor} onClose={(changed) => { setAccessFor(null); if (changed) load(); }} />
      )}

      {showForm && (
        <div className="mb-6">
          <LocationForm initial={editing} onSave={handleSave} onCancel={() => { setShowForm(false); setEditing(null); }} />
        </div>
      )}

      {/* Location cards */}
      {loading ? (
        <div className="text-center py-20 text-slate-400">Loading…</div>
      ) : locations.length === 0 ? (
        <div className="text-center py-20">
          <Building2 size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-semibold">No locations yet</p>
          <p className="text-slate-400 text-sm mt-1">Add your first office or branch to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map(loc => (
            <div key={loc.id}
              className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm cursor-pointer transition-all hover:shadow-md hover:border-indigo-300"
              onClick={() => navigate(`/settings/locations/${loc.id}`)}>

              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <MapPin size={18} className="text-indigo-600" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-800 text-sm">{loc.name}</div>
                    {loc.code && <div className="text-xs text-slate-400 font-mono">{loc.code}</div>}
                  </div>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${loc.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                  {loc.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              {loc.address && (
                <div className="flex items-start gap-2 text-xs text-slate-500 mb-2">
                  <MapPin size={11} className="mt-0.5 shrink-0" /> {loc.address}
                </div>
              )}
              {loc.manager && (
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                  <UserCheck size={11} /> {loc.manager}
                </div>
              )}

              <div className="flex flex-wrap gap-2 mb-4">
                <StatChip icon={Users} value={loc.staff_count} label="staff" color="bg-blue-50 text-blue-600" />
                <StatChip icon={UserCheck} value={loc.client_count} label="clients" color="bg-purple-50 text-purple-600" />
                <StatChip icon={Calendar} value={loc.shifts_last_30} label="shifts (30d)" color="bg-amber-50 text-amber-600" />
              </div>

              <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                <button onClick={() => setAccessFor(loc)}
                  className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-semibold text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50">
                  <ShieldCheck size={12} /> Access
                </button>
                <button onClick={() => { setEditing(loc); setShowForm(true); }}
                  className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
                  <Edit2 size={12} /> Edit
                </button>
                <button onClick={() => toggleActive(loc)}
                  className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-semibold rounded-lg border
                    ${loc.is_active ? 'text-amber-600 border-amber-200 hover:bg-amber-50' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}`}>
                  {loc.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button onClick={() => handleDelete(loc)}
                  className="py-1.5 px-2.5 text-xs text-red-500 border border-red-100 rounded-lg hover:bg-red-50">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
